import type { LoadedItem, ItemUpdateRequestDto } from '@mimir/common/dto/searchResultHit';
import type { GeneratedMediaSignedUrlResponseDto } from '@mimir/common/dto/itemModelPrimitives';
import type { SearchResult } from '@mimir/common/search/interfaces';
import { neverCancel } from '@mimir/common/async/async';
import type { UpdateFolderAccessControlDto } from '@mimir/common/dto/folderModels';
import {
  exceptionEssence,
  makeCollapsedError,
} from '@mimir/common/util/exception';
import type { OperationApiContext } from '@mimir/common/util/operationApiContext';
import type { HttpQueryParameters } from '@mimir/common/util/apiClient';
import { apiGet, apiPost } from '@mimir/common/util/http';
import * as Path from '../aws/api/path';
import { api_createKeldaRequest, api_getKeldaConfig } from '../api/keldaApi';
import { api_createFolder, api_updateFolderAccessControl } from '../api/folderApi';
import { api_createGeneratedMediaUploadUrl, api_getTenantConfig, api_search, api_updateArchiveStatus, api_updateItem } from './api';
import { getEmbed } from '@mimir/common/client/hal';
import type { SearchProps } from './search';
import { buildSearchParams } from './search';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { MediaSegmentsResponse } from '@mimir/common/dto/mediaDtos';
import Axios from 'axios';
import type { KeldaRequestCreationDto } from '@mimir/common/dto/keldaRequestDtos';
import type { TenantConfigDto } from '@mimir/common/dto/tenantModel';
import { OperationType } from '@mimir/common/dto/archiveOperationDto';
import { invariant } from '@mimir/common/util/invariant';
import { assumeResultObject } from '@mimir/common/client/assumeResult';
import type { GlanceDto } from '@mimir/common/dto/opsGlanceDto';
import type { PlayableItemDto, PlayableItemGetOptions } from '@mimir/common/dto/playableItemDto';
import type { SingleTrackPeaksDto } from '@mimir/common/dto/peaksDto';
import type { UrlArrayRequestDto, UrlArrayResponseDto } from '@mimir/common/dto/urlArrayDtos';

function commaSeparatedListOrUndefined(values: string[] | undefined) {
  if (!values) {
    return undefined;
  }

  return values.join(',');
}

export interface VideoApiActions {
  getPlayableItem(itemId: string, options: PlayableItemGetOptions): Promise<PlayableItemDto | null>;
  getMediaSegmentUrls(
    itemId: string,
    streamIds: StreamRoleId[],
    signedForMethod: 'get' | 'put',
    firstSegmentIndex: number
  ): Promise<MediaSegmentsResponse>;
  getUrlArray(request: UrlArrayRequestDto): Promise<UrlArrayResponseDto>;
  // Not provided in the share page since we don't display the waveforms there
  getPeaks?: (itemId: string, audioTrackIndex: number) => Promise<SingleTrackPeaksDto>;
  context?: OperationApiContext; // Not provided in the share page, conveys a cancelToken to activeBuffering.monitorItem
}

/**
 * Higher level abstractions around the Mimir server API, which includes retry,
 * translation of query parameters into requests, and interpretation of results.
 *
 * This is a helper class that wraps OperationApiContext to provide a convenient
 * action oriented API. The ApiAction instances are inexpensive to construct, so
 * it is perfectly fine to only pass OperationApiContext around, and construct
 * an ApiAction object from it when desired.
 */
export class ApiActions implements VideoApiActions {
  constructor(readonly context: OperationApiContext) {}

  async getItem(itemId: string, fieldList?: string[]): Promise<LoadedItem> {
    const fields = commaSeparatedListOrUndefined(fieldList);
    const additionalParams = { ...this.context.getAdditionalParams() };

    if (fields) {
      additionalParams.queryParams = { fields };
    }
    const result = await this.resiliently(
      'getItem',
      { itemId, fieldList },
      () =>
        apiGet(
          this.context.client.getApiClient(),
          Path.ofItem(this.context.client.getServiceLinks(), itemId),
          additionalParams
        )
    );
    return assumeResultObject<LoadedItem>(result);
  }

  async getPeaks(itemId: string, audioTrackId: number): Promise<SingleTrackPeaksDto> {
    const result = await this.resiliently(
      'getPeaks',
      { itemId, audioTrackId },
      () =>
        apiGet(
          this.context.client.getApiClient(),
          Path.ofPeaks(this.context.client.getServiceLinks(), itemId, audioTrackId),
        )
    );
    return assumeResultObject<SingleTrackPeaksDto>(result);
  }

  async getPlayableItem(itemId: string, options: PlayableItemGetOptions): Promise<PlayableItemDto> {
    const result = await this.resiliently(
      'getPlayableItem',
      {},
      () =>
        apiGet(
          this.context.client.getApiClient(),
          Path.ofPlayableItem(this.context.client.getServiceLinks(), itemId),
          {
            queryParams: {
              options: JSON.stringify(options),
            }
          }
        )
    );
    return assumeResultObject<PlayableItemDto>(result);
  }

  async getUrlArray(request: UrlArrayRequestDto): Promise<UrlArrayResponseDto> {
    const result = await this.resiliently(
      'getUrlArray',
      {},
      () =>
        apiPost(
          this.context.client.getApiClient(),
          Path.ofUrlArray(this.context.client.getServiceLinks()),
          request,
        )
    );
    return assumeResultObject<UrlArrayResponseDto>(result);
  }

  async search(searchQuery: SearchProps): Promise<SearchResult> {
    const queryParams = buildSearchParams(searchQuery);
    const result = await this.resiliently('search', { searchQuery }, () =>
      api_search(this.context.client, { queryParams }));
    result.hits = getEmbed(result, 'collection', []);
    result._embedded = undefined;
    return result;
  }

  async getMediaSegmentUrls(
    itemId: string,
    streamIds: StreamRoleId[],
    signedForMethod: 'get' | 'put',
    firstSegmentIndex: number
  ): Promise<MediaSegmentsResponse> {
    const path = Path.ofMediaSegments(
      this.context.client.getServiceLinks(),
      itemId,
      streamIds.join(','),
      signedForMethod,
      '' + firstSegmentIndex
    );
    const result = await this.resiliently(
      'getMediaSegmentUrls',
      { itemId, streamIds, signedForMethod, firstSegmentIndex },
      () =>
        apiGet(this.context.client.getApiClient(), path, {
          ...this.context.getAdditionalParams(),
        })
    );
    return assumeResultObject<MediaSegmentsResponse>(result);
  }

  async createFolder(folderName: string, parentFolderId: string, isHidden = false) {
    const result = await this.resiliently(
      'createFolder',
      { folderName, parentFolderId, isHidden },
      () =>
        api_createFolder(
          this.context.client,
          folderName,
          parentFolderId,
          isHidden
        )
    );
    return result;
  }

  async updateItem(itemId: string, changes: ItemUpdateRequestDto) {
    const result = await this.resiliently(
      'updateItem',
      { itemId, changes },
      () =>
        api_updateItem(
          this.context.client,
          itemId,
          changes,
          { ...this.context.getAdditionalParams() }
        )
    );
    return result;
  }

  async updateFolderAccessControl(folderId: string, accessControl: UpdateFolderAccessControlDto, queryParams: HttpQueryParameters = {}) {
    const result = await this.resiliently(
      'updateFolderAccessControl',
      { folderId, ...accessControl, queryParams },
      () =>
        api_updateFolderAccessControl(
          this.context.client,
          folderId,
          accessControl,
          queryParams
        )
    );
    return result;
  }

  async archiveItem(itemId: string) {
    return api_updateArchiveStatus(this.context.client, itemId, { type: OperationType.Archive });
  }

  async createKeldaRequest(body: KeldaRequestCreationDto) {
    return api_createKeldaRequest(this.context.client, body);
  }

  async getKeldaConfig(keldaId: string) {
    const result = await this.resiliently(
      'getKeldaConfig',
      { keldaId },
      () =>
        api_getKeldaConfig(
          this.context.client,
          keldaId
        )
    );
    return result;
  }

  async getTenantConfig(): Promise<TenantConfigDto> {
    const result = await this.resiliently(
      'getTenantConfig',
      {},
      () =>
        api_getTenantConfig(
          this.context.client,
        )
    );
    return result;
  }

  async getThumbnailUploadUrl(itemId: string): Promise<GeneratedMediaSignedUrlResponseDto> {
    return api_createGeneratedMediaUploadUrl(this.context.client, itemId, 'thumbnail');
  }

  async getOpsGlance(): Promise<GlanceDto> {
    const path = this.context.client.getServiceLinks().opsGlance;
    invariant(path, 'Expected opsGlance link');
    const result = await this.resiliently(
      'getOpsGlance',
      {},
      () => apiGet(this.context.client.getApiClient(), path, { ...this.context.getAdditionalParams() })
    );
    return assumeResultObject<GlanceDto>(result);
  }

  private async resiliently<T>(
    what: string,
    info: Record<string, unknown>,
    action: () => Promise<T>
  ): Promise<T> {
    try {
      return await this.context.resilience.resiliently(
        action,
        this.context.options.cancelToken ?? neverCancel,
        this.context.options.beforeRetryInterlude
      );
    } catch (cause) {
      if (Axios.isCancel(cause)) {
        throw cause;
      }

      throw makeCollapsedError(
        'ApiActionError',
        exceptionEssence(cause) + ', during ' + what + ' action',
        { cause, info }
      );
    }
  }
}
